import { useId, useState, type FormEvent } from "react";
import {
  CALLBACK_INVALID_REASONS,
  type CallbackSlotsResponse,
  type CallbackSoonPresetCode,
  type CreateCallbackBody,
  type LeadCardResponse,
  type TaskView,
} from "@gioia365/contracts";
import { callbackInstantOf } from "@gioia365/domain/callbacks";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ApiRequestError } from "../../api/http";
import { Button } from "../../design/components/Button";
import { Checkbox } from "../../design/components/Checkbox";
import { Dialog } from "../../design/components/Dialog";
import { Reason } from "../../design/components/Reason";
import { TextField } from "../../design/components/TextField";
import { useToast } from "../../design/components/Toast";
import { messageForError } from "../../i18n/errors";
import { formatNumericDate, formatNumericDateTime, formatNumericTime, localDayOf } from "../../i18n/format";
import { useT, type Translate } from "../../i18n/locale";
import { canActOnTaskOf, taskScopeOf, useAccess, type Access } from "../../session/access";
import type { TabContext } from "../../session/tab-context";
import { newRequestId } from "../../telephony/call-session-state";
import { callbackSlotsQuery, leadCardQuery, useCreateCallback, useLeadView, useRescheduleTask } from "../lead-queries";
import "./callback.css";

// La finestra «Richiama dopo» (piano M5, Task 17, D13; mock-up `scheda-lead.html`, decisione del 25 settembre 2026): una finestra
// sopra la pagina, così la postazione e la scheda non si spostano. In alto «Concordato con il Lead» (parte sempre vuota, anche
// per «Sposta»: la spunta chi chiama; D13, ruling Q56), poi «Tra qualche ora» con i 16 blocchi («+30 min» … «+8 h») e il loro
// orario già calcolato, «Nei prossimi giorni» con i due giorni lavorativi a mezz'ore («Domani, giovedì 08/10/2026»), e «Altro
// giorno e ora» a quarti d'ora. Gli orari li calcola il server da adesso e li rivalida al salvataggio: un orario rifiutato (`422
// callback_slot_invalid`) dice il motivo, uno per codice. Il Lead ha al più un richiamo aperto: se c'è già, la finestra lo dice
// («C'è già un richiamo per …: lo sposti?») e l'orario scelto lo sposta (`PATCH`); un `409 task_exists` arrivato nel frattempo
// rilegge la scheda e porta allo stesso punto, e un richiamo da spostare che nel frattempo si è chiuso lascia lo stesso orario a
// un richiamo nuovo. Dalla postazione il richiamo nasce dalla chiamata (`sourceCallId`), l'esito si salva prima (`beforeSave`) e
// la scheda si rilegge prima di scegliere fra «Sposta» e un richiamo nuovo, perché la chiamata appena finita completa il richiamo
// che c'era (ruling Q55). Tutte le ore nel fuso dell'azienda.

/** Le chiamate al Lead sono ferme: lo status le blocca, oppure ogni suo telefono è bloccato o segnato errato. */
export function callsBlocked(card: LeadCardResponse): boolean {
  const { calling } = card;
  if (calling.phones.length === 0) return calling.reason === "status_blocks";
  return calling.phones.every((phone) => phone.reason === "status_blocks" || phone.reason === "phone_wrong");
}

/**
 * Il contesto della scheda descrive la sua vista: nella «Guarda come…» il server applica anche i permessi dei Task della persona
 * osservata, che porta il contesto della vista; finché il contesto non è quello della vista, non si offre niente.
 */
function contextOfView(access: Access, view: string | null): boolean {
  return (access.context.commercialView?.membershipId ?? null) === view;
}

/** Chi guarda può fissare un richiamo su questo Lead: `tasks.create` (anche della persona osservata) e la modifica del Lead (il server decide comunque). */
export function canSetCallback(access: Access, card: LeadCardResponse, view: string | null): boolean {
  return contextOfView(access, view) && card.permissions.canUpdate && taskScopeOf(access, "create") === "all";
}

/** Chi guarda può spostare questo richiamo: `tasks.update` su tutti o sui propri, con la regola del server anche nella «Guarda come…». */
export function canMoveCallback(access: Access, task: TaskView, view: string | null): boolean {
  return contextOfView(access, view) && canActOnTaskOf(access, "update", task.assignee.membershipId);
}

/** La finestra serve a chi guarda: fissa un richiamo sul Lead, oppure sposta quello aperto se il Lead ne ha già uno. */
export function canCallBack(access: Access, card: LeadCardResponse, view: string | null): boolean {
  return card.openTask ? canMoveCallback(access, card.openTask, view) : canSetCallback(access, card, view);
}

/**
 * Il richiamo aperto che la chiamata appena finita completa (D12, ruling Q55): una chiamata in uscita arrivata al Lead completa,
 * quando il server la chiude con l'evento del fornitore, il richiamo fissato l'ultima volta (`setAt`, la nascita o l'ultimo
 * «Sposta») prima che lei nascesse, con la stessa regola del server (`completeByCall`). La scheda letta al riaggancio può non
 * saperlo ancora, e per la postazione quel richiamo non c'è già più; uno spostato durante la chiamata resta il richiamo del Lead.
 */
export function completedByCall(task: TaskView, call: { startedAt: string }): boolean {
  return Date.parse(task.setAt) < Date.parse(call.startedAt);
}

/** Il richiamo aperto della scheda. */
function openTaskOf(card: LeadCardResponse | undefined): TaskView | null {
  return card?.openTask?.status === "open" ? card.openTask : null;
}

/** Un «Sposta» rifiutato perché il richiamo non è più aperto o non c'è più: la chiamata lo ha completato, o qualcuno lo ha chiuso. */
function taskGone(error: unknown): boolean {
  return error instanceof ApiRequestError && (error.code === "invalid_state" || error.code === "task_not_found");
}

type IsoWeekday = "1" | "2" | "3" | "4" | "5" | "6" | "7";
const ISO_WEEKDAYS = ["7", "1", "2", "3", "4", "5", "6"] as const satisfies readonly IsoWeekday[];

/** Il giorno di calendario `2026-10-08` come data UTC, per l'aritmetica dei giorni (un giorno di calendario non ha fuso). */
function calendarDate(day: string, offset = 0): Date {
  const [year = 1970, month = 1, date = 1] = day.split("-").map(Number);
  return new Date(Date.UTC(year, month - 1, date + offset));
}

/** Il giorno della settimana ISO (1 lunedì … 7 domenica) di un giorno di calendario, per i nomi dei giorni nei cataloghi. */
function isoWeekdayOf(day: string): IsoWeekday {
  return ISO_WEEKDAYS[calendarDate(day).getUTCDay()] ?? "1";
}

/** Il nome di un blocco dal suo codice, con le parole del mock-up: `+30m` «+30 min», `+1h` «+1 h», `+1h30` «+1 h 30». */
function presetText(t: Translate, preset: CallbackSoonPresetCode): string {
  const match = /^\+(?:(\d+)h)?(30)?m?$/.exec(preset);
  const hours = Number(match?.[1] ?? 0);
  if (hours === 0) return t("callbacks.preset.minutes", { minutes: 30 });
  return t(match?.[2] === undefined ? "callbacks.preset.hours" : "callbacks.preset.hoursHalf", { hours });
}

type Props = {
  tab: TabContext;
  card: LeadCardResponse;
  timeZone: string;
  /** Dalla postazione: la chiamata da cui nasce il richiamo, e l'esito da salvare prima (`false`: non si va avanti). */
  fromCall?: { callId: string; startedAt: string; beforeSave: () => Promise<boolean> };
  /** Il richiamo è fissato o spostato. */
  onSaved?: (task: TaskView) => void;
  onClose: () => void;
};

type Preset = CreateCallbackBody["preset"];

/** Il motivo di un orario rifiutato dal server, dal codice; le ore dei richiami sono quelle della finestra (il primo e l'ultimo orario di un giorno). */
function refusalText(t: Translate, error: unknown, slots: CallbackSlotsResponse | undefined, timeZone: string): string {
  if (error instanceof ApiRequestError && error.code === "callback_slot_invalid") {
    const reason = CALLBACK_INVALID_REASONS.find((code) => code === error.details?.reason);
    if (reason === "outside_hours") return hoursText(t, slots, timeZone) ?? messageForError(t, error);
    if (reason) return t(`callbacks.invalid.${reason}`);
  }
  return messageForError(t, error);
}

function hoursText(t: Translate, slots: CallbackSlotsResponse | undefined, timeZone: string): string | null {
  const day = slots?.days[0]?.slots;
  const from = day?.[0];
  const to = day?.at(-1);
  return from && to ? t("callbacks.invalid.outside_hours", { from: formatNumericTime(timeZone, from), to: formatNumericTime(timeZone, to) }) : null;
}

export function CallbackDialog({ tab, card, timeZone, fromCall, onSaved, onClose }: Props) {
  const t = useT();
  const toast = useToast();
  const access = useAccess();
  const view = useLeadView();
  const queryClient = useQueryClient();
  const leadId = card.lead.id;
  const ids = useId();
  const slots = useQuery(callbackSlotsQuery(tab, leadId, view));
  const create = useCreateCallback(tab, leadId, view);
  const reschedule = useRescheduleTask(tab, leadId, view);
  const open = openTaskOf(card);
  // Dalla postazione il richiamo che la chiamata completa non si propone di spostare: non c'è già più.
  const existing = open && !(fromCall && completedByCall(open, fromCall)) ? open : null;
  // «Lo sposti?» e «Sposta» soltanto per un richiamo che chi guarda può spostare: quello di un collega, arrivato mentre la finestra era aperta, il server lo rifiuterebbe.
  const movable = existing !== null && canMoveCallback(access, existing, view);
  // Una chiave per apertura: un nuovo invio dopo un guasto è lo stesso richiamo (ruling Q54).
  const [requestId] = useState(newRequestId);
  // «Concordato con il Lead» parte sempre vuota, anche per «Sposta» (D13, ruling Q56): la spunta chi fissa o sposta.
  const [agreed, setAgreed] = useState(false);
  const [customOpen, setCustomOpen] = useState(false);
  const [custom, setCustom] = useState<{ day: string; time: string } | null>(null);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const data = slots.data;
  const today = localDayOf(timeZone, new Date().toISOString());
  const customValue = custom ?? (data ? { day: localDayOf(timeZone, data.custom.earliest), time: formatNumericTime(timeZone, data.custom.earliest) } : { day: "", time: "" });

  /** L'orario di un blocco: l'ora, e il giorno della settimana quando cade in un altro giorno («gio 09:00», mock-up). */
  function soonLabel(at: string): string {
    const day = localDayOf(timeZone, at);
    const time = formatNumericTime(timeZone, at);
    return day === today ? time : t("callbacks.otherDay", { weekday: t(`callbacks.weekdayShort.${isoWeekdayOf(day)}`), time });
  }

  /** Il titolo di un giorno: «Domani, giovedì 08/10/2026» per domani, altrimenti «Venerdì 09/10/2026» (mock-up). */
  function dayTitle(day: string): string {
    const weekday = isoWeekdayOf(day);
    const date = formatNumericDate(timeZone, day);
    return day === calendarDate(today, 1).toISOString().slice(0, 10)
      ? t("callbacks.tomorrow", { weekday: t(`callSequenceSettings.weekday.${weekday}`), date })
      : t("callbacks.dayTitle", { weekday: t(`calendar.weekdays.name.${weekday}`), date });
  }

  /** Sposta il richiamo `target`, oppure, senza, ne fissa uno nuovo. */
  async function write(target: TaskView | null, at: string, preset: Preset): Promise<TaskView> {
    if (target) return (await reschedule.mutateAsync({ taskId: target.id, body: { expectedVersion: target.version, at, agreedWithLead: agreed } })).task;
    const body: CreateCallbackBody = { at, preset, agreedWithLead: agreed, ...(fromCall ? { sourceCallId: fromCall.callId } : {}), note: null, requestId };
    return (await create.mutateAsync(body)).task;
  }

  async function pick(at: string, preset: Preset) {
    if (pending) return;
    setError(null);
    setPending(true);
    try {
      if (fromCall && !(await fromCall.beforeSave())) {
        onClose();
        return;
      }
      // Dalla postazione la scheda si rilegge, e si aspetta: il richiamo che la chiamata ha già completato non c'è più (uno
      // nuovo), quello ancora aperto si sposta se chi guarda lo può spostare (spostato, la chiamata non lo completa più). Dalla
      // testata, lo stesso: il richiamo di un collega non si sposta, anche se è apparso dopo un `409 task_exists`.
      const current = fromCall ? openTaskOf(await queryClient.fetchQuery({ ...leadCardQuery(tab, leadId, view), staleTime: 0 })) : existing;
      const target = current && canMoveCallback(access, current, view) ? current : null;
      let task: TaskView;
      try {
        task = await write(target, at, preset);
      } catch (failure) {
        // Il richiamo da spostare si è chiuso nel frattempo e la scheda riletta non ne ha un altro: lo stesso orario è un richiamo nuovo.
        if (target === null || !taskGone(failure) || openTaskOf(queryClient.getQueryData(leadCardQuery(tab, leadId, view).queryKey)) !== null) throw failure;
        task = await write(null, at, preset);
      }
      toast.show(t("callbacks.saved", { when: formatNumericDateTime(timeZone, task.dueAt) }), "success");
      onSaved?.(task);
      onClose();
    } catch (failure) {
      setPending(false);
      setError(refusalText(t, failure, data, timeZone));
    }
  }

  function submitCustom(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const instant = callbackInstantOf(customValue.day, customValue.time, timeZone);
    // Un'ora che il cambio dell'ora salta non è mai fra le ore dei richiami.
    if (!instant) {
      setError(hoursText(t, data, timeZone) ?? t("errors.callback_slot_invalid"));
      return;
    }
    void pick(instant.toISOString(), "custom");
  }

  const customId = `${ids}-custom`;
  return (
    <Dialog open size="wide" title={t("callbacks.title")} onClose={onClose}>
      <div className="g-callback" aria-busy={pending}>
        {existing ? <Reason>{t(movable ? "callbacks.exists" : "callbacks.existsNoMove", { when: formatNumericDateTime(timeZone, existing.dueAt) })}</Reason> : null}
        {error ? (
          <Reason tone="warning" role="alert">
            {error}
          </Reason>
        ) : null}
        <Checkbox id={`${ids}-agreed`} label={t("callbacks.agreed")} hint={t("callbacks.agreedHint")} checked={agreed} onChange={setAgreed} />
        {slots.isPending ? <p className="g-field__hint">{t("common.loading")}</p> : null}
        {slots.isError ? (
          <div className="g-callback__row">
            <Reason tone="warning" role="alert">
              {messageForError(t, slots.error)}
            </Reason>
            <Button disabled={slots.isFetching} onClick={() => void slots.refetch()}>
              {t("common.retry")}
            </Button>
          </div>
        ) : null}
        {data ? (
          <>
            <section className="g-callback__block" aria-labelledby={`${ids}-soon`}>
              <h3 id={`${ids}-soon`} className="g-callback__title">
                {t("callbacks.soon")}
              </h3>
              <div className="g-callback__times" data-kind="soon">
                {data.soon.map((slot) => (
                  <Button key={slot.preset} className="g-callback__time" disabled={pending} onClick={() => void pick(slot.at, slot.preset)}>
                    <strong>{presetText(t, slot.preset)}</strong> <span>{soonLabel(slot.at)}</span>
                  </Button>
                ))}
              </div>
            </section>
            <section className="g-callback__block" aria-labelledby={`${ids}-days`}>
              <h3 id={`${ids}-days`} className="g-callback__title">
                {t("callbacks.days")}
              </h3>
              {data.days.map((day) => (
                <div key={day.day} className="g-callback__day" role="group" aria-labelledby={`${ids}-${day.day}`}>
                  <p id={`${ids}-${day.day}`} className="g-callback__day-name">
                    {dayTitle(day.day)}
                  </p>
                  <div className="g-callback__times" data-kind="day">
                    {day.slots.map((at) => (
                      <Button key={at} className="g-callback__time" disabled={pending} onClick={() => void pick(at, "day_slot")}>
                        {formatNumericTime(timeZone, at)}
                      </Button>
                    ))}
                  </div>
                </div>
              ))}
            </section>
            <section className="g-callback__block">
              <Button className="g-callback__custom-toggle" aria-expanded={customOpen} aria-controls={customOpen ? customId : undefined} onClick={() => setCustomOpen((open) => !open)}>
                {t("callbacks.custom")}
              </Button>
              {customOpen ? (
                <form id={customId} className="g-callback__custom" onSubmit={submitCustom} noValidate>
                  <TextField
                    id={`${ids}-day`}
                    label={t("callbacks.customDay")}
                    type="date"
                    min={localDayOf(timeZone, data.custom.earliest)}
                    max={data.custom.latestDay}
                    value={customValue.day}
                    onChange={(event) => setCustom({ ...customValue, day: event.target.value })}
                  />
                  <TextField
                    id={`${ids}-time`}
                    label={t("callbacks.customTime")}
                    type="time"
                    step={data.custom.stepMinutes * 60}
                    value={customValue.time}
                    onChange={(event) => setCustom({ ...customValue, time: event.target.value })}
                  />
                  <Button variant="primary" type="submit" disabled={pending || customValue.day === "" || customValue.time === ""}>
                    {movable ? t("callbacks.move") : t("callbacks.save")}
                  </Button>
                </form>
              ) : null}
            </section>
          </>
        ) : null}
      </div>
    </Dialog>
  );
}
